import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import API_URL from "../config";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  FaMoneyBillWave,
  FaWallet,
  FaBalanceScale,
  FaBoxOpen,
  FaBoxes,
  FaFilter,
} from "react-icons/fa";

import "./home.css";

// Couleurs Répartition dépenses
const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4"];

export default function Stats({ entrepriseId, typeUser }) {
  const DEBUG = false;
  const log = (...args) => {
    if (DEBUG) console.log("[DBG]", ...args);
  };

  // ✅ Empêche l'auto-sélection après un "Clear Site" ou un choix manuel
  const userChangedSiteRef = useRef(false);

  // ---- États pour les données principales ----
  const [expenses, setExpenses] = useState([]);
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [deletedSales, setDeletedSales] = useState([]); // ✅ ventes supprimées (table supprimer_vente)
  const [deletedStocks, setDeletedStocks] = useState([]);

  // ---- États pour la caisse et les sites ----
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("all");
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState(null);

  // Caisse globale et par site
  const [caissePrincipale, setCaissePrincipale] = useState(0);
  const [caisseParSite, setCaisseParSite] = useState([]);

  // ---- États pour les filtres et l'UI ----
  const [availableMonths, setAvailableMonths] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableDays, setAvailableDays] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedDay, setSelectedDay] = useState("all");
  const [selectedDate, setSelectedDate] = useState("all");

  const [expenseSearchTerm, setExpenseSearchTerm] = useState("");
  const [expenseSortField, setExpenseSortField] = useState("date");
  const [expenseSortOrder, setExpenseSortOrder] = useState("desc");

  const [paymentFilter, setPaymentFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [saleSortField, setSaleSortField] = useState("date_vente");
  const [saleSortOrder, setSaleSortOrder] = useState("desc");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showExpenses, setShowExpenses] = useState(true);
  const [showSales, setShowSales] = useState(true);
  const [showDeletedSales, setShowDeletedSales] = useState(true); // ✅ affichage ventes supprimées
  const [showDeletedStocks, setShowDeletedStocks] = useState(true);

  // ---------------- Helpers ----------------
  const ensureArray = (x) => (Array.isArray(x) ? x : []);
  const isCredit = (t) =>
    (t ?? "")
      .toString()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .includes("credit");

  const safeNumber = (v) => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
  };

  const saleAmount = (s) =>
    safeNumber(
      s?.montant ??
        safeNumber(s?.prix_unitaire || s?.prix) * safeNumber(s?.quantite || s?.quantity)
    );

  const toISO = (d) => {
    if (!d && d !== 0) return null;
    if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;

    const date = new Date(d);
    if (!isNaN(date.getTime())) {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }

    const m = String(d).trim().match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (m) {
      const dd = String(m[1]).padStart(2, "0");
      const mm = String(m[2]).padStart(2, "0");
      const yyyy = m[3];
      return `${yyyy}-${mm}-${dd}`;
    }
    return null;
  };

  const formatDate = (d) => {
    try {
      const iso = toISO(d);
      if (!iso) return d ?? "";
      return new Date(iso).toLocaleDateString("fr-FR");
    } catch {
      return d ?? "";
    }
  };

  const getItemSiteId = (item) => {
    if (!item) return null;
    const cand = item.siteId ?? item.site_id ?? null;
    return cand !== undefined && cand !== null ? String(cand) : null;
  };

  const getSelectedSiteInfo = useCallback(() => {
    if (!selectedSite || selectedSite === "all") return { siteId: null, siteName: null };
    const [kind, val] = selectedSite.split(":", 2);
    if (!val) return { siteId: null, siteName: null };
    if (kind === "id")
      return {
        siteId: val,
        siteName: sites.find((s) => String(s.id) === val)?.nom || null,
      };
    if (kind === "name")
      return {
        siteId: sites.find((s) => s.nom === val)?.id || null,
        siteName: val,
      };
    return { siteId: null, siteName: null };
  }, [selectedSite, sites]);

  const matchesSelectedSite = useCallback(
    (item) => {
      const itemId = getItemSiteId(item);
      const { siteId: selectedId } = getSelectedSiteInfo();
      if (!selectedId) return true; // all sites
      if (!itemId) return true; // si item n'a pas siteId, on ne filtre pas
      return String(itemId) === String(selectedId);
    },
    [getSelectedSiteInfo]
  );

  const buildApiUrl = useCallback(
    (path, extraParams = {}) => {
      const base = API_URL;
      const params = new URLSearchParams();

      if (entrepriseId) params.set("entrepriseId", String(entrepriseId));

      const { siteId } = getSelectedSiteInfo();
      if (siteId && siteId !== "all") params.set("siteId", String(siteId));

      Object.entries(extraParams).forEach(([k, v]) => {
        if (v !== undefined && v !== null) params.set(k, String(v));
      });

      const qs = params.toString();
      const separator = path.includes("?") ? "&" : "?";
      return `${base}${path}${qs ? separator + qs : ""}`;
    },
    [entrepriseId, getSelectedSiteInfo]
  );

  // ---------------- Fetch sites ----------------
  useEffect(() => {
    if (!entrepriseId) {
      setSites([]);
      setSelectedSite("all");
      return;
    }

    let mounted = true;
    setSitesLoading(true);
    setSitesError(null);

    const fetchSites = async () => {
      try {
        // ✅ IMPORTANT: ne pas utiliser buildApiUrl ici (sinon dépend du selectedSite)
        const url = `${API_URL}/api/sites/${entrepriseId}`;
        const res = await axios.get(url);

        if (!mounted) return;

        const data = ensureArray(res?.data);
        setSites(data);

        // ✅ Auto-select seulement si l'utilisateur n'a pas déjà touché le filtre
        if (!userChangedSiteRef.current && data.length === 1 && selectedSite === "all") {
          const s = data[0];
          const id = s.id ?? s._id ?? null;
          const name = s.nom || s.name || s.label || String(id || "");
          if (id) setSelectedSite(`id:${String(id)}`);
          else setSelectedSite(`name:${String(name)}`);
        }
      } catch (err) {
        if (!mounted) return;
        console.error("❌ Erreur lors du chargement des sites :", err);
        setSites([]);
        setSitesError("Impossible de récupérer les sites. Vérifiez la connexion.");
      } finally {
        if (mounted) setSitesLoading(false);
      }
    };

    fetchSites();
    return () => {
      mounted = false;
    };
  }, [entrepriseId, selectedSite]);

  // 1) caisse principale
  useEffect(() => {
    if (!entrepriseId) return setCaissePrincipale(0);
    const fetchCaisse = async () => {
      try {
        const url = `${API_URL}/api/caisse/caisse-principale/${entrepriseId}`;
        const res = await axios.get(url);
        setCaissePrincipale(safeNumber(res.data?.solde || 0));
      } catch (err) {
        console.error("❌ Erreur fetch caisse principale:", err);
        setCaissePrincipale(0);
      }
    };
    fetchCaisse();
  }, [entrepriseId]);

  // 2) dépenses
  useEffect(() => {
    if (!entrepriseId || typeUser === "gestionnaire") return setExpenses([]);
    let mounted = true;

    const fetchExpenses = async () => {
      try {
        const url = buildApiUrl(`/api/depenses/${entrepriseId}`);
        const res = await axios.get(url);
        if (!mounted) return;
        setExpenses(ensureArray(res?.data));
      } catch (err) {
        if (!mounted) return;
        console.error("❌ Erreur fetch dépenses:", err);
        setExpenses([]);
      }
    };

    fetchExpenses();
    return () => {
      mounted = false;
    };
  }, [entrepriseId, typeUser, buildApiUrl]);

  // 3) caisse gestionnaire par site
  useEffect(() => {
    if (!entrepriseId || sites.length === 0) return;

    const fetchCaisseGestionnaire = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/caisse/${entrepriseId}`);
        const caisseData = ensureArray(res.data);

        const caisseParSiteComputed = sites.map((site) => {
          const montantSite = caisseData.find((c) => c.site_id === site.id);
          return { ...site, montant: safeNumber(montantSite?.montant) };
        });
        setCaisseParSite(caisseParSiteComputed);
      } catch (err) {
        console.error("Erreur récupération caisse gestionnaire:", err);
        setCaisseParSite([]);
      }
    };
    fetchCaisseGestionnaire();
  }, [entrepriseId, sites]);

  // 7) produits & stocks
  useEffect(() => {
    if (!entrepriseId) {
      setProducts([]);
      setStocks([]);
      return;
    }
    let mounted = true;

    const fetchItems = async (path, setter) => {
      try {
        const url = buildApiUrl(path);
        const res = await axios.get(url);
        if (mounted) setter(ensureArray(res.data));
      } catch (err) {
        if (mounted) setter([]);
        console.error(`❌ Erreur fetch ${path}:`, err);
      }
    };

    fetchItems(`/api/produits`, setProducts);
    fetchItems(`/api/stocks`, setStocks);

    return () => {
      mounted = false;
    };
  }, [entrepriseId, buildApiUrl]);

  // 4) ventes
  useEffect(() => {
    if (!entrepriseId) {
      setSales([]);
      setLoading(false);
      return;
    }

    let mounted = true;
    setError(null);

    const fetchSales = async () => {
      const { siteId } = getSelectedSiteInfo();

      // ✅ IMPORTANT: si aucun site sélectionné, pas de loading infini
      if (!siteId) {
        if (mounted) {
          setSales([]);
          setLoading(false);
        }
        return;
      }

      if (mounted) setLoading(true);

      try {
        const url = `${API_URL}/api/sales/${entrepriseId}/${siteId}`;
        const res = await axios.get(url);

        if (!mounted) return;
        const data = ensureArray(res?.data);

        const enriched = data.map((sale) => {
          const montant = saleAmount(sale);
          const pid = sale.produit_id ?? sale.product_id ?? sale.productId;
          const p = products.find((pp) => String(pp.id) === String(pid));
          const produitNom = sale.produit || sale.produit_nom || p?.nom || "Inconnu";
          return { ...sale, produitNom, montant };
        });

        setSales(enriched);
      } catch (err) {
        if (!mounted) return;
        console.error("❌ Erreur fetch ventes :", err);
        setError("Impossible de récupérer les ventes.");
        setSales([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchSales();
    return () => {
      mounted = false;
    };
  }, [entrepriseId, selectedSite, products, getSelectedSiteInfo]);

  // ✅ 5) ventes supprimées (depuis table supprimer_vente) — support "all sites"
  useEffect(() => {
    if (!entrepriseId) return setDeletedSales([]);
    let mounted = true;

    const fetchDeletedSales = async () => {
      try {
        const { siteId } = getSelectedSiteInfo();

        // ✅ all sites -> endpoint sans siteId
        const url = siteId
          ? `${API_URL}/api/sales/supprime/${entrepriseId}/${siteId}`
          : `${API_URL}/api/sales/supprime/${entrepriseId}`;

        const res = await axios.get(url);
        if (!mounted) return;
        setDeletedSales(ensureArray(res.data));
      } catch (err) {
        if (!mounted) return;
        console.error("❌ Erreur fetch ventes supprimées :", err);
        setDeletedSales([]);
      }
    };

    fetchDeletedSales();
    return () => {
      mounted = false;
    };
  }, [entrepriseId, selectedSite, getSelectedSiteInfo]);

  // 6) stocks supprimés
  useEffect(() => {
    if (!entrepriseId) return setDeletedStocks([]);
    let mounted = true;

    const fetchDeletedStocks = async () => {
      const { siteId } = getSelectedSiteInfo();
      if (!siteId) {
        if (mounted) setDeletedStocks([]);
        return;
      }
      try {
        const url = `${API_URL}/api/stocks/supprime/${entrepriseId}/${siteId}`;
        const res = await axios.get(url);
        if (!mounted) return;
        setDeletedStocks(ensureArray(res.data));
      } catch (err) {
        if (!mounted) return;
        console.error("❌ Erreur fetch stocks supprimés :", err);
        setDeletedStocks([]);
      }
    };

    fetchDeletedStocks();
    return () => {
      mounted = false;
    };
  }, [entrepriseId, selectedSite, getSelectedSiteInfo]);

  // ---------------- Filtrage / calculs ----------------
  const expensesBySite = useMemo(() => {
    if (selectedSite === "all") return ensureArray(expenses);
    return ensureArray(expenses).filter((e) => matchesSelectedSite(e));
  }, [expenses, selectedSite, matchesSelectedSite]);

  const productsBySite = useMemo(
    () => ensureArray(products).filter((p) => matchesSelectedSite(p)),
    [products, matchesSelectedSite]
  );

  const stocksBySite = useMemo(
    () => ensureArray(stocks).filter((st) => matchesSelectedSite(st)),
    [stocks, matchesSelectedSite]
  );

  const salesBySite = useMemo(
    () => ensureArray(sales).filter((s) => matchesSelectedSite(s)),
    [sales, matchesSelectedSite]
  );

  // options mois/année
  useEffect(() => {
    const months = new Set();
    const years = new Set();
    const days = new Set();

    const collect = (dateStr) => {
      const iso = toISO(dateStr);
      if (!iso) return;
      const d = new Date(iso);
      months.add(d.getMonth() + 1);
      years.add(d.getFullYear());
      days.add(d.getDate());
    };

    ensureArray(expensesBySite).forEach((e) => collect(e.date));
    ensureArray(salesBySite).forEach((s) => collect(s.date_vente));

    setAvailableMonths([...months].sort((a, b) => a - b));
    setAvailableYears([...years].sort((a, b) => a - b));
    setAvailableDays([...days].sort((a, b) => a - b));
  }, [expensesBySite, salesBySite]);

  const filteredSalesBase = useMemo(() => {
    return salesBySite.filter((s) => {
      const iso = toISO(s.date_vente);
      if (!iso) return false;

      if (selectedDate !== "all") return iso === selectedDate;
      const d = new Date(iso);
      if (selectedMonth !== "all" && d.getMonth() + 1 !== Number(selectedMonth)) return false;
      if (selectedYear !== "all" && d.getFullYear() !== Number(selectedYear)) return false;
      if (selectedDay !== "all" && d.getDate() !== Number(selectedDay)) return false;

      return true;
    });
  }, [salesBySite, selectedMonth, selectedYear, selectedDay, selectedDate]);

  const filteredSales = useMemo(() => {
    const term = (searchTerm || "").trim().toLowerCase();
    return filteredSalesBase
      .filter((s) => {
        if (paymentFilter === "credit") return isCredit(s.type_paiement);
        if (paymentFilter === "cash") return !isCredit(s.type_paiement);
        return true;
      })
      .filter((s) => {
        if (!term) return true;
        const fields = [
          s.produitNom,
          s.type_paiement,
          s.client || s.client_nom || "",
          s.reference || s.ref || "",
        ].map((x) => String(x || "").toLowerCase());
        return fields.some((f) => f.includes(term));
      });
  }, [filteredSalesBase, paymentFilter, searchTerm]);

  const filteredExpensesBase = useMemo(() => {
    return expensesBySite.filter((e) => {
      const iso = toISO(e.date);
      if (!iso) return false;

      if (selectedDate !== "all") return iso === selectedDate;
      const d = new Date(iso);
      if (selectedMonth !== "all" && d.getMonth() + 1 !== Number(selectedMonth)) return false;
      if (selectedYear !== "all" && d.getFullYear() !== Number(selectedYear)) return false;
      if (selectedDay !== "all" && d.getDate() !== Number(selectedDay)) return false;

      return true;
    });
  }, [expensesBySite, selectedMonth, selectedYear, selectedDay, selectedDate]);

  const filteredExpensesTable = useMemo(() => {
    const term = (expenseSearchTerm || "").trim().toLowerCase();
    return filteredExpensesBase.filter((e) => {
      if (!term) return true;
      const fields = [e.nom || "", e.categorie || "", e.reference || ""].map((x) => String(x).toLowerCase());
      return fields.some((f) => f.includes(term));
    });
  }, [filteredExpensesBase, expenseSearchTerm]);

  const totalExpenses = filteredExpensesBase.reduce((sum, e) => sum + safeNumber(e.montant || 0), 0);
  const totalSales = filteredSalesBase.reduce((sum, s) => sum + saleAmount(s), 0);
  const totalCredits = filteredSalesBase
    .filter((s) => isCredit(s.type_paiement))
    .reduce((sum, s) => sum + saleAmount(s), 0);

  const recettes = totalSales - totalCredits;
  const gain = recettes - totalExpenses;

  const totalProducts = productsBySite.length;
  const totalProductsSold = filteredSalesBase.reduce((sum, s) => sum + safeNumber(s.quantite || s.quantity), 0);

  const sortedExpenses = useMemo(() => {
    const dir = expenseSortOrder === "asc" ? 1 : -1;
    const arr = [...filteredExpensesTable];
    arr.sort((a, b) => {
      const getVal = (ex) => {
        switch (expenseSortField) {
          case "montant":
            return safeNumber(ex.montant || 0);
          case "nom":
            return (ex.nom || "").toString().toLowerCase();
          default:
            return new Date(toISO(ex.date)).getTime() || 0;
        }
      };
      const va = getVal(a);
      const vb = getVal(b);
      if (typeof va === "string" || typeof vb === "string") return va.toString().localeCompare(vb.toString()) * dir;
      return (va - vb) * dir;
    });
    return arr;
  }, [filteredExpensesTable, expenseSortField, expenseSortOrder]);

  const sortedSales = useMemo(() => {
    const dir = saleSortOrder === "asc" ? 1 : -1;
    const arr = [...filteredSales];
    arr.sort((a, b) => {
      const getVal = (s) => {
        switch (saleSortField) {
          case "montant":
            return saleAmount(s);
          case "quantite":
            return safeNumber(s.quantite || s.quantity || 0);
          case "produitNom":
            return (s.produitNom || "").toString().toLowerCase();
          default:
            return new Date(toISO(s.date_vente)).getTime() || 0;
        }
      };
      const va = getVal(a);
      const vb = getVal(b);
      if (typeof va === "string" || typeof vb === "string") return va.toString().localeCompare(vb.toString()) * dir;
      return (va - vb) * dir;
    });
    return arr;
  }, [filteredSales, saleSortField, saleSortOrder]);

  // Graphs
  const dailyExpenses = useMemo(() => {
    const acc = [];
    filteredExpensesBase.forEach((e) => {
      const iso = toISO(e.date);
      if (!iso) return;
      const d = new Date(iso);
      const day = d.getDate();
      const existing = acc.find((x) => x.day === day);
      if (existing) existing.amount += safeNumber(e.montant || 0);
      else acc.push({ day, amount: safeNumber(e.montant || 0) });
    });
    acc.sort((a, b) => a.day - b.day);
    return acc;
  }, [filteredExpensesBase]);

  const dailySales = useMemo(() => {
    const acc = [];
    filteredSalesBase.forEach((s) => {
      const iso = toISO(s.date_vente);
      if (!iso) return;
      const d = new Date(iso);
      const day = d.getDate();
      const amt = saleAmount(s);
      const existing = acc.find((x) => x.day === day);
      if (existing) existing.amount += amt;
      else acc.push({ day, amount: amt });
    });
    acc.sort((a, b) => a.day - b.day);
    return acc;
  }, [filteredSalesBase]);

  const monthlyExpenses = useMemo(() => {
    const acc = [];
    ensureArray(expensesBySite)
      .filter((e) => selectedYear === "all" || toISO(e.date)?.slice(0, 4) === String(selectedYear))
      .forEach((e) => {
        const iso = toISO(e.date);
        if (!iso) return;
        const m = Number(iso.slice(5, 7));
        const existing = acc.find((d) => d.month === m);
        if (existing) existing.amount += safeNumber(e.montant || 0);
        else acc.push({ month: m, amount: safeNumber(e.montant || 0) });
      });
    acc.sort((a, b) => a.month - b.month);
    return acc;
  }, [expensesBySite, selectedYear]);

  const monthlySales = useMemo(() => {
    const acc = [];
    ensureArray(salesBySite)
      .filter((s) => selectedYear === "all" || toISO(s.date_vente)?.slice(0, 4) === String(selectedYear))
      .forEach((s) => {
        const iso = toISO(s.date_vente);
        if (!iso) return;
        const m = Number(iso.slice(5, 7));
        const amt = saleAmount(s);
        const existing = acc.find((d) => d.month === m);
        if (existing) existing.amount += amt;
        else acc.push({ month: m, amount: amt });
      });
    acc.sort((a, b) => a.month - b.month);
    return acc;
  }, [salesBySite, selectedYear]);

  const resetFilters = () => {
    setSelectedMonth("all");
    setSelectedYear("all");
    setSelectedDay("all");
    setSelectedDate("all");
    setPaymentFilter("all");
    setSearchTerm("");
    setExpenseSearchTerm("");
  };

  const pieData = useMemo(() => {
    return filteredExpensesBase.reduce((acc, e) => {
      const name = e.nom || "Autre";
      const found = acc.find((x) => x.name === name);
      if (found) found.value += safeNumber(e.montant || 0);
      else acc.push({ name, value: safeNumber(e.montant || 0) });
      return acc;
    }, []);
  }, [filteredExpensesBase]);

  return (
    <div className="container">
      <h1 className="title">Tableau de Bord Financier</h1>

      {/* --- Section Caisses --- */}
      <div className="caisse-section-container card-shadow">
        <div className="caisse-row">
          <div className="caisse-row-left">
            <h2 className="caisse-title">Caisse Principale Globale</h2>
            <p className="caisse-hint">Solde global de l’entreprise</p>
          </div>

          <span className="main-caisse-solde">
            {safeNumber(caissePrincipale).toLocaleString("fr-FR")} FCFA
          </span>
        </div>

        <h3 className="caisse-subtitle">Caisses Gestionnaires par Site</h3>

        <div className="caisse-grid">
          {caisseParSite.map((site) => (
            <div key={site.id} className="site-caisse-card">
              <div className="site-caisse-top">
                <span className="site-name-bold">{site.nom || `Site ${site.id}`}</span>
              </div>
              <span className="site-caisse-solde">
                {safeNumber(site.montant).toLocaleString("fr-FR")} FCFA
              </span>
            </div>
          ))}
          {caisseParSite.length === 0 && (
            <div className="site-caisse-card site-caisse-card-empty">
              <span className="site-name-bold">Aucun site</span>
              <span className="site-caisse-solde">—</span>
            </div>
          )}
        </div>
      </div>

      {/* --- Filtre de Site et Période --- */}
      <div className="filters-container card-shadow">
        <div className="filters-header-row">
          <h3 className="filters-header">
            <FaFilter /> Filtrage (Site & Période)
          </h3>
          <button onClick={resetFilters} className="reset-btn">
            Réinitialiser tout
          </button>
        </div>

        {/* Filtre Site */}
        <div className="filters-site-row">
          <select
            value={selectedSite}
            onChange={(e) => {
              userChangedSiteRef.current = true;
              setSelectedSite(e.target.value);
            }}
            className="select"
            title={sitesLoading ? "Chargement..." : sitesError ? sitesError : "Filtrer par site"}
          >
            <option value="all">Tous les sites</option>
            {ensureArray(sites).map((s) => {
              const id = s.id ?? s._id ?? s.siteId ?? s.id_site ?? s.site_id ?? null;
              const name = s.nom || s.name || s.label || s.nom_site || s.site_name || String(id ?? "Site");
              return (
                <option key={id ?? name} value={id ? `id:${id}` : `name:${name}`}>
                  {name}
                </option>
              );
            })}
          </select>

          <button
            onClick={() => {
              userChangedSiteRef.current = true;
              setSelectedSite("all");
            }}
            className="clear-btn"
          >
            Clear Site
          </button>

          {sitesLoading && <span className="badge-info">chargement…</span>}
          {sitesError && <span className="badge-error">{sitesError}</span>}
        </div>

        {/* Filters période */}
        <div className="filters-period-row">
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="select-small">
            <option value="all">Tous les mois</option>
            {availableMonths.map((m) => (
              <option key={m} value={m}>
                Mois {m}
              </option>
            ))}
          </select>

          <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="select-small">
            <option value="all">Toutes les années</option>
            {availableYears.map((y) => (
              <option key={y} value={y}>
                Année {y}
              </option>
            ))}
          </select>

          <select
            value={selectedDay}
            onChange={(e) => setSelectedDay(e.target.value)}
            className="select-small"
            disabled={availableDays.length === 0}
          >
            <option value="all">Tous les jours</option>
            {availableDays.map((d) => (
              <option key={d} value={d}>
                Jour {d}
              </option>
            ))}
          </select>

          <div className="date-row">
            <input
              type="date"
              value={selectedDate === "all" ? "" : selectedDate}
              onChange={(e) => setSelectedDate(e.target.value || "all")}
              className="date-input"
            />
            <button onClick={() => setSelectedDate("all")} className="clear-btn-small">
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="stats-grid">
        {typeUser === "entreprise" && (
          <div className="card-small kpi-card">
            <FaBoxes size={22} className="kpi-icon kpi-icon-primary" />
            <h4>Produits Actifs (Site)</h4>
            <p>{totalProducts}</p>
          </div>
        )}

        <div className="card-small kpi-card">
          <FaWallet size={22} className="kpi-icon kpi-icon-blue" />
          <h4>Recettes (Comptant)</h4>
          <p>{safeNumber(recettes).toLocaleString("fr-FR")} FCFA</p>
        </div>

        {typeUser === "entreprise" && (
          <div className="card-small kpi-card">
            <FaBalanceScale size={22} className={`kpi-icon ${gain >= 0 ? "text-success" : "text-danger"}`} />
            <h4>Gain Net</h4>
            <p>{safeNumber(gain).toLocaleString("fr-FR")} FCFA</p>
          </div>
        )}

        <div className="card-small kpi-card">
          <FaBoxOpen size={22} className="kpi-icon kpi-icon-blue" />
          <h4>Qté Vendue</h4>
          <p>{totalProductsSold}</p>
        </div>

        {typeUser !== "gestionnaire" && (
          <div className="card-small kpi-card">
            <FaMoneyBillWave size={22} className="kpi-icon kpi-icon-red" />
            <h4>Total Dépenses</h4>
            <p>{safeNumber(totalExpenses).toLocaleString("fr-FR")} FCFA</p>
          </div>
        )}
      </div>

      {/* Section Ventes */}
      <div className="sales-section card-shadow">
        <div className="sales-header" onClick={() => setShowSales(!showSales)}>
          <h2 className="sales-section-title">🧾 Ventes du Site ({sortedSales.length})</h2>
          <span className="toggle-icon">{showSales ? "▲" : "▼"}</span>
          {loading && sortedSales.length === 0 && <span className="badge-info">chargement..</span>}
          {error && <span className="badge-error">{error}</span>}
        </div>

        {showSales && (
          <>
            <div className="controls-row">
              <input
                type="text"
                placeholder="Rechercher (produit, client, référence)…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />

              <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)} className="select">
                <option value="all">Tous les paiements</option>
                <option value="cash">Comptant</option>
                <option value="credit">Crédit</option>
              </select>

              <select value={saleSortField} onChange={(e) => setSaleSortField(e.target.value)} className="select-small">
                <option value="date_vente">Tri: Date</option>
                <option value="montant">Tri: Montant</option>
                <option value="quantite">Tri: Quantité</option>
                <option value="produitNom">Tri: Produit</option>
              </select>

              <select value={saleSortOrder} onChange={(e) => setSaleSortOrder(e.target.value)} className="select-small">
                <option value="desc">Ordre: Décroissant</option>
                <option value="asc">Ordre: Croissant</option>
              </select>
            </div>

            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th className="th">Date</th>
                    <th className="th">Produit</th>
                    <th className="th">Qté</th>
                    <th className="th text-right">Montant</th>
                    <th className="th">Paiement</th>
                    <th className="th">Client</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSales.map((s, i) => {
                    const montant = saleAmount(s);
                    const key = s.id || s._id || `${s.date_vente}-${i}`;
                    const credit = isCredit(s.type_paiement);
                    return (
                      <tr key={key} className="tr">
                        <td className="td">{formatDate(s.date_vente)}</td>
                        <td className="td">{s.produitNom || "—"}</td>
                        <td className="td text-right">{safeNumber(s.quantite || s.quantity)}</td>
                        <td className="td text-right text-bold">{montant.toLocaleString("fr-FR")} FCFA</td>
                        <td className="td">
                          <span className={credit ? "badge-warning" : "badge-success"}>
                            {s.type_paiement || (credit ? "Crédit" : "Comptant")}
                          </span>
                        </td>
                        <td className="td">{s.client || s.client_nom || "—"}</td>
                      </tr>
                    );
                  })}

                  {sortedSales.length === 0 && !loading && (
                    <tr>
                      <td className="td-empty" colSpan={6}>
                        Aucune vente pour les filtres sélectionnés.
                      </td>
                    </tr>
                  )}
                </tbody>

                {sortedSales.length > 0 && (
                  <tfoot>
                    <tr>
                      <td className="td-foot" colSpan={3}>
                        Total des ventes affichées:
                      </td>
                      <td className="td-foot text-right">
                        {sortedSales.reduce((sum, s) => sum + saleAmount(s), 0).toLocaleString("fr-FR")} FCFA
                      </td>
                      <td className="td-foot" colSpan={2}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        )}
      </div>

      {/* ✅ Historique suppression ventes */}
      <div className="sales-section card-shadow">
        <div className="sales-header" onClick={() => setShowDeletedSales(!showDeletedSales)}>
          <h3 className="sales-section-title">🗑️ Historique de suppression des ventes ({deletedSales.length})</h3>
          <span className="toggle-icon">{showDeletedSales ? "▲" : "▼"}</span>
        </div>

        {showDeletedSales && (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th className="th">Date vente</th>
                  <th className="th">Produit ID</th>
                  <th className="th text-right">Qté</th>
                  <th className="th text-right">PU</th>
                  <th className="th">Paiement</th>
                  <th className="th">Client</th>
                  <th className="th">Motif</th>
                  <th className="th">Supprimé le</th>
                </tr>
              </thead>

              <tbody>
                {deletedSales.length > 0 ? (
                  deletedSales.map((v, idx) => (
                    <tr key={v.id ?? idx} className="tr">
                      <td className="td">{formatDate(v.date_vente)}</td>
                      <td className="td">{v.produit_id ?? "—"}</td>
                      <td className="td text-right">{safeNumber(v.quantite)}</td>
                      <td className="td text-right">{safeNumber(v.prix_unitaire).toLocaleString("fr-FR")} FCFA</td>
                      <td className="td">{v.type_paiement || "—"}</td>
                      <td className="td">{v.client || "—"}</td>
                      <td className="td">{v.motif || "—"}</td>
                      <td className="td">{formatDate(v.deleted_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="td-empty">
                      Aucune vente supprimée trouvée pour ce filtre.
                    </td>
                  </tr>
                )}
              </tbody>

              {deletedSales.length > 0 && (
                <tfoot>
                  <tr>
                    <td className="td-foot" colSpan={3}>
                      Total (ventes supprimées)
                    </td>
                    <td className="td-foot text-right" colSpan={5}>
                      {deletedSales
                        .reduce((sum, v) => sum + safeNumber(v.quantite) * safeNumber(v.prix_unitaire), 0)
                        .toLocaleString("fr-FR")}{" "}
                      FCFA
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* Section Dépenses */}
      {typeUser !== "gestionnaire" && (
        <div className="sales-section card-shadow">
          <div className="sales-header" onClick={() => setShowExpenses(!showExpenses)}>
            <h2 className="sales-section-title">💸 Dépenses Filtrées ({sortedExpenses.length})</h2>
            <span className="toggle-icon">{showExpenses ? "▲" : "▼"}</span>
          </div>

          {showExpenses && (
            <>
              <div className="controls-row">
                <input
                  type="text"
                  placeholder="Rechercher (nom, catégorie, référence)…"
                  value={expenseSearchTerm}
                  onChange={(e) => setExpenseSearchTerm(e.target.value)}
                  className="search-input"
                />

                <select value={expenseSortField} onChange={(e) => setExpenseSortField(e.target.value)} className="select-small">
                  <option value="date">Tri: Date</option>
                  <option value="montant">Tri: Montant</option>
                  <option value="nom">Tri: Nom</option>
                </select>

                <select value={expenseSortOrder} onChange={(e) => setExpenseSortOrder(e.target.value)} className="select-small">
                  <option value="desc">Ordre: Décroissant</option>
                  <option value="asc">Ordre: Croissant</option>
                </select>
              </div>

              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="th">Date</th>
                      <th className="th">Nom</th>
                      <th className="th">Catégorie</th>
                      <th className="th text-right">Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedExpenses.map((e, i) => {
                      const key = e.id || e._id || `${e.date}-${i}`;
                      return (
                        <tr key={key} className="tr">
                          <td className="td">{formatDate(e.date)}</td>
                          <td className="td">{e.nom || "—"}</td>
                          <td className="td">{e.categorie || "—"}</td>
                          <td className="td text-right text-bold text-danger">
                            {safeNumber(e.montant || 0).toLocaleString("fr-FR")} FCFA
                          </td>
                        </tr>
                      );
                    })}

                    {sortedExpenses.length === 0 && (
                      <tr>
                        <td className="td-empty" colSpan={4}>
                          Aucune dépense pour les filtres sélectionnés.
                        </td>
                      </tr>
                    )}
                  </tbody>

                  {sortedExpenses.length > 0 && (
                    <tfoot>
                      <tr>
                        <td className="td-foot" colSpan={3}>
                          Total des dépenses affichées:
                        </td>
                        <td className="td-foot text-right text-danger">
                          {sortedExpenses.reduce((sum, e) => sum + safeNumber(e.montant || 0), 0).toLocaleString("fr-FR")} FCFA
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Historique suppression stocks */}
      <div className="sales-section card-shadow">
        <div className="sales-header" onClick={() => setShowDeletedStocks(!showDeletedStocks)}>
          <h3 className="sales-section-title">📦 Historique de suppression des stocks ({deletedStocks.length})</h3>
          <span className="toggle-icon">{showDeletedStocks ? "▲" : "▼"}</span>
        </div>

        {showDeletedStocks && (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th className="th">Produit</th>
                  <th className="th text-right">Qté Initiale</th>
                  <th className="th text-right">Stock restant (avant supp.)</th>
                  <th className="th">Supprimé par</th>
                  <th className="th">Date suppression</th>
                </tr>
              </thead>
              <tbody>
                {deletedStocks.length > 0 ? (
                  deletedStocks.map((st, idx) => (
                    <tr key={idx} className="tr">
                      <td className="td">{st.produit_nom || "—"}</td>
                      <td className="td text-right">{st.quantite}</td>
                      <td className="td text-right">{st.stock_restant}</td>
                      <td className="td">
                        {st.supprime_par} ({st.type_user})
                      </td>
                      <td className="td">{formatDate(st.date_suppression)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="td-empty">
                      Aucun stock supprimé trouvé pour ce site.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Graphiques */}
      <div className="charts-scroll-container">
        <div className="charts-grid-scroll">
          {typeUser !== "gestionnaire" && (
            <div className="chart-box card-shadow">
              <h2 className="chart-title">Dépenses par jour</h2>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={dailyExpenses}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="amount" stroke="#EF4444" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="chart-box card-shadow">
            <h2 className="chart-title">Ventes par jour</h2>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={dailySales}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="amount" stroke="#10B981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {typeUser !== "gestionnaire" && (
            <div className="chart-box card-shadow">
              <h2 className="chart-title">Dépenses par mois</h2>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={monthlyExpenses}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="amount" fill="#EF4444" name="Dépenses" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="chart-box card-shadow">
            <h2 className="chart-title">Ventes par mois</h2>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={monthlySales}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="amount" fill="#10B981" name="Ventes" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {typeUser !== "gestionnaire" && (
            <div className="chart-box card-shadow chart-box-pie">
              <h2 className="chart-title">Répartition des dépenses (filtrées)</h2>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={105}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {pieData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${Number(value || 0).toLocaleString("fr-FR")} FCFA`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
